import {
  AfterViewChecked,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  signal
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { EMPTY, Subject, Subscription, merge } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  take,
  tap,
  timeout
} from 'rxjs/operators';

import { CognitoAuthActions, CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { AuthFacade } from '@cpd-app/state/auth';
import { MilitaryBranch } from '@cpd-app/core-module/models';
import { MilitaryBranchService } from '@cpd-app/core-module/services/military/military-branch.service';
import { NavigationService } from '@cpd-app/core-module/services';
import { RegistrationDraftService } from '@cpd-app/core-module/services/auth/registration-draft.service';
import { FeatureFlagService } from '@cpd-app/core-module/services/feature-flag/feature-flag.service';

import { LoginDialogView } from './login-dialog-view.model';

/** Below this, a typeahead is guessing rather than searching. 01-account section 7. */
const INSTALLATION_MIN_CHARS = 2;

/** Long enough that a member does not out-type the network, short enough to feel live. */
const INSTALLATION_DEBOUNCE_MS = 250;

/**
 * How long to wait for the branch list before offering to carry on without it.
 *
 * MilitaryBranchService.getBranchList wraps its fetch in a hand-rolled
 * Observable whose inner subscribe has no error handler, so a failed query
 * neither emits nor errors: it just never arrives. An error callback alone
 * would never fire, and the member would sit on "Loading" forever.
 */
const BRANCH_LIST_TIMEOUT_MS = 8_000;

/** 01-account section 7: resend is rate limited, and says when the last one went. */
const RESEND_COOLDOWN_MS = 30_000;

/**
 * Sign in, register, and counselor sign in: one dialog, six views.
 *
 * 01-account section 4 settles that this is a dialog rather than a page,
 * because being asked to sign in interrupts something and closing it should put
 * the member back rather than somewhere new. The page behind supplies the top
 * bar and the nav; this carries no shell of its own.
 *
 * One card is visible at a time so there is never a second form underneath the
 * one being filled in. Views are a state machine rather than routes: the
 * member's place in it does not deserve a URL, and a route would take the page
 * behind away.
 *
 * Learners and counselors are two entries that share a shell, not a role toggle
 * on one form: a toggle would put a password field one tap away from a member
 * who will never have one.
 */
@Component({
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss']
})
export class LoginDialogComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  public readonly view = signal<LoginDialogView>('signin');
  public readonly isViaIntroOpen = signal(false);

  /** The single polite live region for the dialog. 00-foundations section 6. */
  public readonly announcement = signal('');

  public readonly branches = signal<MilitaryBranch[]>([]);
  public readonly branchesFailed = signal(false);
  public readonly installationMatches = signal<{ id: string; name: string }[]>(
    []
  );
  public readonly selectedBranch = signal<MilitaryBranch>(null);
  public readonly selectedInstallationId = signal<string>(null);

  public readonly sentToAddress = signal('');
  public readonly lastSentAt = signal<Date>(null);
  public readonly isResendBlocked = signal(false);
  public readonly counselorError = signal('');

  /**
   * A failed magic-link send used to be completely silent: the dialog stayed
   * on the same view with no spinner, no error and no way to tell whether
   * anything had been sent. This is what the banner reads.
   */
  public readonly magicLinkError = signal('');

  /** The counselor password field, revealed or not. */
  public readonly isPasswordHidden = signal(true);

  public readonly emailFormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email])
  });

  public readonly identityFormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email])
  });

  public readonly serviceFormGroup = new FormGroup({
    // Free text is allowed: an unlisted posting must not be a dead end, so the
    // control holds what was typed and selectedInstallationId holds the match
    // when there is one.
    installation: new FormControl('', [Validators.required]),
    // No default. 01-account section 5: an empty month is a real answer, and a
    // prefilled one is a guess the member did not make.
    separationMonth: new FormControl('')
  });

  /** The heading is the dialog's accessible name, so it changes with the view. */
  public readonly heading = computed(() => {
    switch (this.view()) {
      case 'sent':
        return 'Check your email';
      case 'reg1':
        return 'Create your account';
      case 'reg2':
        return 'Where do you serve?';
      case 'counselor':
        // One title for both: the staff side is one workspace now (#1890), so
        // there is no longer a separate dashboard to name.
        return 'Counselor and admin sign in';
      case 'done':
        return 'You are in';
      case 'continue':
        return 'Finish signing in';
      case 'used':
        return 'This link no longer works';
      case 'returnExpired':
        return 'This link has expired';
      case 'claim':
        return 'Add your email';
      default:
        return 'Sign in or create an account';
    }
  });

  @ViewChild('viewHeading') private viewHeading: ElementRef<HTMLElement>;

  private readonly installationTerm$ = new Subject<string>();
  private readonly subscription = new Subscription();
  /** The outcome listener for the send in flight, replaced by the next send. */
  private magicLinkAttempt: Subscription;
  private resendTimer: ReturnType<typeof setTimeout>;

  /**
   * Focus moves to the new heading, but only once per view change: this runs on
   * every check, and re-grabbing focus would trap a member inside the heading.
   */
  private focusedView: LoginDialogView = null;

  /** AUTH.7. The address an emailed link was for, shown on its two cards. */
  public readonly landedEmail = signal('');

  /** AUTH.7. True from the press of "Continue" until the sign-in settles. */
  public readonly isContinuing = signal(false);

  /** Held in memory only, and dropped once spent or refused. */
  private landedToken: string | null = null;
  private continueAttempt: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: {
      openViaIntro?: boolean;
      startAt?: LoginDialogView;
      magicLink?: { token: string; email: string };
    },
    private cognitoAuthFacade: CognitoAuthFacade,
    private authFacade: AuthFacade,
    private militaryBranchService: MilitaryBranchService,
    private navigationService: NavigationService,
    private draft: RegistrationDraftService,
    private dialogRef: MatDialogRef<LoginDialogComponent>,
    private featureFlags: FeatureFlagService,
    private actions$: Actions
  ) {}

  /**
   * ANALYTICS.2 / PWS 4.4.1.2.2. Carried over from the standalone counselor
   * page this view replaced: counselor accounts are provisioned, so somebody
   * arriving without one had nowhere to go and no way for us to count that
   * they had asked.
   *
   * This is NOT the access-request mutation 01-account section 6 voided. That
   * was a new write this redesign would have added; this is an existing
   * flagged route, and dropping it would drop a separate requirement. The
   * service fails closed, so an unreachable backend hides the link rather than
   * offering a route whose mutation would be refused anyway.
   */
  public readonly dantesReportingEnabled$ = this.featureFlags.isEnabled$(
    'DANTES_REPORTING_ENABLED'
  );

  public ngOnInit(): void {
    this.isViaIntroOpen.set(!!this.data?.openViaIntro);
    this.restoreDraft();

    if (this.data?.magicLink?.token && this.data.magicLink.email) {
      this.landedToken = this.data.magicLink.token;
      this.landedEmail.set(this.data.magicLink.email);
      // "Sign in another way" should not make them type the address again.
      this.emailFormGroup.patchValue({ email: this.data.magicLink.email });
    }

    // An opener can point straight at a view: /account/register lands on reg1
    // rather than making the member find "Create one" first.
    if (this.data?.startAt) {
      this.go(this.data.startAt);
    }

    this.subscription.add(
      this.installationTerm$
        .pipe(
          debounceTime(INSTALLATION_DEBOUNCE_MS),
          distinctUntilChanged(),
          tap((term) => this.searchInstallations(term))
        )
        .subscribe()
    );

    this.subscription.add(
      this.militaryBranchService.installationLocations$.subscribe((results) =>
        this.installationMatches.set(
          (results || [])
            .filter((location) => !!location?.id)
            .map((location) => ({
              id: String(location.id),
              name: location.name
            }))
        )
      )
    );

    this.subscription.add(
      this.authFacade.logInFailure$
        .pipe(tap(() => this.counselorError.set('Invalid email or password!')))
        .subscribe()
    );

    /*
     * AD-1. Close on success, mirroring the failure subscription above.
     *
     * There was no success branch, and no success observable on the facade to
     * subscribe to. The effect navigates into the staff workspace while this dialog
     * -- a CDK overlay, independent of the router outlet -- stays exactly where
     * it is, covering the page it just took the counselor to.
     */
    this.subscription.add(
      this.authFacade.logInSuccess$
        .pipe(tap(() => this.dialogRef?.close()))
        .subscribe()
    );
  }

  public ngAfterViewChecked(): void {
    if (this.focusedView === this.view()) {
      return;
    }

    this.focusedView = this.view();
    this.viewHeading?.nativeElement?.focus();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
    clearTimeout(this.resendTimer);
  }

  // --- Moving between views ------------------------------------------------

  public go(view: LoginDialogView): void {
    this.view.set(view);
    this.announce(this.heading());

    if (view === 'reg2' && !this.branches().length) {
      this.loadBranches();
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  // --- Learner: magic link -------------------------------------------------

  public onEmailFormSubmit(): void {
    if (!this.emailFormGroup.valid) {
      // WCAG 3.3.1: the button was disabled, so an empty field did nothing
      // and said nothing. Now the field says what it needs.
      this.emailFormGroup.markAllAsTouched();
      return;
    }

    this.sendMagicLink(this.emailFormGroup.controls.email.value);
  }

  public resend(): void {
    if (this.isResendBlocked()) {
      return;
    }

    this.sendMagicLink(this.sentToAddress());
  }

  /**
   * What the member sees while the resend is on cooldown. Section 7: say when
   * the last one went rather than silently doing nothing.
   */
  public readonly resendLabel = computed(() =>
    this.isResendBlocked()
      ? 'Link sent. You can ask again shortly'
      : 'Send another link'
  );

  /**
   * BOTH outcomes are listened for, and the listener dies with the dialog.
   *
   * This used to subscribe to success alone, and off `this.subscription`: a
   * rejected send left the member on the same view forever with nothing on
   * screen, and a success arriving after the dialog closed called `go('sent')`
   * on a destroyed component. Merged and taken once, so exactly one outcome is
   * handled and the subscription completes itself; the previous attempt is
   * dropped first so a resend does not leave a second listener behind.
   */
  private sendMagicLink(email: string): void {
    if (!email) {
      return;
    }

    this.magicLinkError.set('');
    this.magicLinkAttempt?.unsubscribe();

    this.magicLinkAttempt = merge(
      this.cognitoAuthFacade.signInWithMagicLinkSuccess$.pipe(map(() => true)),
      this.actions$.pipe(
        ofType(CognitoAuthActions.signInWithMagicLinkFailure),
        map(() => false)
      )
    )
      .pipe(take(1))
      .subscribe((wasSent) => {
        if (!wasSent) {
          this.magicLinkError.set(
            'We could not send that link just now. Check the address and try again.'
          );
          this.announce('The sign-in link could not be sent.');
          return;
        }

        this.sentToAddress.set(email);
        this.lastSentAt.set(new Date());
        this.blockResend();
        this.go('sent');
        this.announce(`A sign-in link has been sent to ${email}.`);
      });

    this.subscription.add(this.magicLinkAttempt);

    this.cognitoAuthFacade.signInWithMagicLink(email);
  }

  // --- Learner: arriving from an emailed link (AUTH.7) --------------------

  /**
   * Spend the link now that a person has asked to. Exactly one outcome is
   * handled, as in sendMagicLink:
   *
   * - signed in: confirmSignInWithMagicLinkSuccess, or a status check that
   *   finds this browser was already signed in (a consumed link reopened);
   * - used or expired: Cognito refuses the answer as NotAuthorizedException;
   * - anything else: say so, and leave the button to try again.
   *
   * The opening-in-a-new-browser path (SignInException, then a fresh signIn
   * and a second confirmation) emits none of these in between, so the one
   * taken is the final one.
   */
  public continueSignIn(): void {
    if (!this.landedToken || this.isContinuing()) {
      return;
    }

    this.magicLinkError.set('');
    this.isContinuing.set(true);
    this.continueAttempt?.unsubscribe();

    this.continueAttempt = this.actions$
      .pipe(
        ofType(
          CognitoAuthActions.confirmSignInWithMagicLinkSuccess,
          CognitoAuthActions.confirmSignInWithMagicLinkFailure,
          CognitoAuthActions.signInWithMagicLinkFailure,
          CognitoAuthActions.checkAuthenticationStatusSuccess
        ),
        filter(
          (action) =>
            action.type !==
              CognitoAuthActions.checkAuthenticationStatusSuccess.type ||
            !!(action as { isAuthenticated?: boolean }).isAuthenticated
        ),
        take(1)
      )
      .subscribe((action) => {
        this.isContinuing.set(false);

        if (
          action.type ===
            CognitoAuthActions.confirmSignInWithMagicLinkSuccess.type ||
          action.type === CognitoAuthActions.checkAuthenticationStatusSuccess.type
        ) {
          this.landedToken = null;
          this.close();
          return;
        }

        const error = (action as { error?: { name?: string } }).error;
        if (
          action.type ===
            CognitoAuthActions.confirmSignInWithMagicLinkFailure.type &&
          error?.name === 'NotAuthorizedException'
        ) {
          this.landedToken = null;
          this.go('used');
          return;
        }

        this.magicLinkError.set(
          'We could not sign you in just now. Try again, or ask for a new link.'
        );
        this.announce('Signing in did not finish.');
      });

    this.subscription.add(this.continueAttempt);

    this.cognitoAuthFacade.confirmSignInWithMagicLink(
      this.landedToken,
      this.landedEmail()
    );
  }

  /** From the "used" card: a fresh link to the same address. */
  public requestNewLink(): void {
    this.sendMagicLink(this.landedEmail());
  }

  private blockResend(): void {
    this.isResendBlocked.set(true);
    clearTimeout(this.resendTimer);
    this.resendTimer = setTimeout(
      () => this.isResendBlocked.set(false),
      RESEND_COOLDOWN_MS
    );
  }

  // --- Learner: registration ----------------------------------------------

  public onIdentitySubmit(): void {
    this.identityFormGroup.markAllAsTouched();

    if (!this.identityFormGroup.invalid) {
      this.draft.patch({
        name: this.identityFormGroup.controls.name.value,
        email: this.identityFormGroup.controls.email.value
      });
      this.go('reg2');
    }
  }

  public onBranchSelected(branch: MilitaryBranch): void {
    this.selectedBranch.set(branch);
    this.draft.patch({ branchCode: branch.branchCode });
    this.installationMatches.set([]);
    this.announce(`${branch.branchTitle} selected.`);
  }

  public onInstallationInput(value: string): void {
    // Typing past a chosen match makes the match stale, so drop the id and keep
    // the text: what is on screen is what will be saved.
    this.selectedInstallationId.set(null);
    this.installationTerm$.next(value);
  }

  public onInstallationChosen(match: { id: string; name: string }): void {
    this.selectedInstallationId.set(match.id);
    this.serviceFormGroup.controls.installation.setValue(match.name);
    this.installationMatches.set([]);
  }

  public onServiceSubmit(): void {
    this.serviceFormGroup.markAllAsTouched();

    // Branch is required, unless its list never arrived: see loadBranches.
    if (!this.selectedBranch() && !this.branchesFailed()) {
      return;
    }

    if (this.serviceFormGroup.invalid) {
      return;
    }

    this.draft.patch({
      installationId: this.selectedInstallationId(),
      installationName: this.serviceFormGroup.controls.installation.value,
      separationMonth: this.serviceFormGroup.controls.separationMonth.value
    });

    // The account does not exist until the emailed link is opened, so this
    // sends the link rather than claiming the account is ready. What was
    // collected here is written to the profile on the first authenticated load.
    const email =
      this.draft.read().email || this.identityFormGroup.controls.email.value;

    this.sendMagicLink(email);
  }

  private loadBranches(): void {
    this.subscription.add(
      this.militaryBranchService
        .getBranchList()
        .pipe(
          timeout(BRANCH_LIST_TIMEOUT_MS),
          // A branch list that will not load must not strand somebody in the
          // middle of registering. The survey asks for branch again anyway.
          catchError(() => {
            this.branchesFailed.set(true);
            return EMPTY;
          })
        )
        .subscribe((branches) => {
          this.branches.set(branches || []);
          this.branchesFailed.set(!branches?.length);
        })
    );
  }

  private searchInstallations(term: string): void {
    if (!term || term.trim().length < INSTALLATION_MIN_CHARS) {
      this.installationMatches.set([]);
      return;
    }

    const branchCode = this.selectedBranch()?.branchCode;

    // installationLocations requires a branch. Until one is picked there is
    // nothing to search, and the free-text answer still stands on its own.
    if (!branchCode) {
      return;
    }

    this.militaryBranchService.getInstallationLocations(
      branchCode,
      term.trim()
    );
  }

  private restoreDraft(): void {
    const held = this.draft.read();

    if (held.name || held.email) {
      this.identityFormGroup.patchValue({
        name: held.name || '',
        email: held.email || ''
      });
    }

    if (held.installationName || held.separationMonth) {
      this.serviceFormGroup.patchValue({
        installation: held.installationName || '',
        separationMonth: held.separationMonth || ''
      });
      this.selectedInstallationId.set(held.installationId || null);
    }
  }

  // --- Counselor -----------------------------------------------------------

  public onCounselorSubmit(email: string, password: string): void {
    this.counselorError.set('');

    if (!email || !password) {
      return;
    }

    this.authFacade.logIn(email, password);
  }

  /**
   * Show or hide the counselor's password.
   *
   * Carried over from the standalone page: without it, somebody typing a
   * provisioned password on a phone keyboard has no way to check what they
   * typed. The button reports its own state through aria-pressed, so it is not
   * only the icon that says whether the password is showing.
   */
  public togglePasswordVisibility(event: MouseEvent): void {
    this.isPasswordHidden.set(!this.isPasswordHidden());
    event.stopPropagation();
  }

  public forgotPassword(): void {
    this.close();
    this.navigationService.forgotPassword();
  }

  // --- Social --------------------------------------------------------------

  public signinWithGoogle(): void {
    this.cognitoAuthFacade.signinWithGoogle();
  }

  public signinWithLinkedIn(): void {
    this.cognitoAuthFacade.signinWithLinkedIn();
  }

  private announce(message: string): void {
    this.announcement.set(message);
  }
}
